import * as rxjs from 'rxjs';
import * as Elevated from '@pitaman71/omniglot-introspect';
import * as Clients from './Clients';
import * as Mutations from './Mutations';
import { Objects, Operations, Properties, Relations, Stores, Streams } from '.';

export interface Status {
    authorized?: boolean;
    initializing?: boolean;
    synchronizing?: boolean;
    dirty?: boolean;
};

export function combineStatus(...statuses: Status[]) {
    let result:Status = statuses.length === 0 ? {} : statuses[0];
    for(let status of statuses.slice(1)) {
        result.authorized &&= status.authorized;
        result.initializing ||= status.initializing;
        result.synchronizing ||= status.synchronizing;
        result.dirty ||= status.dirty;
    }
    return result;
}

export abstract class Stateful<MutationType extends Mutations.BaseMutation> {
    abstract getChanges(): MutationType[];
    abstract revert(): Promise<any>;
    abstract commit(): Promise<any>;

    static fromPropertyStreams(... streams: PropertyStream<any>[]) {
        const result: Stateful<Mutations.BaseMutation>[] = [];
        for(let stream of streams) {
            const stateful = stream.scalar?.stateful() || stream.set?.stateful() || stream.sequence?.stateful() || stream.map?.stateful();
            if(stateful) result.push(stateful);
        }
        return result;
    }
    
    static fromRelationStreams(... streams: RelationStream<any>[]) {
        const result: Stateful<Mutations.BaseMutation>[] = [];
        for(let stream of streams) {
            const stateful = stream.stateful();
            if(stateful) result.push(stateful);
        }
        return result;
    }
}

export interface Persistent<MutationType extends Mutations.BaseMutation> {
    save(peerId: string, ... suffix: MutationType[]): Promise<any>;
}

export class Subscriptions {
    _subscription: rxjs.Subscription|undefined;
    add(subscription_: rxjs.Subscription|Subscriptions) {
        let subscription = 
            subscription_ instanceof rxjs.Subscription 
            ? subscription_
            : subscription_._subscription;
        if(this._subscription === undefined) {
            this._subscription = subscription;
        } else {
            this._subscription.add(subscription);
        }
    }
    unsubscribe() {
        if(this._subscription)
            this._subscription.unsubscribe();
    }
}

export interface Pullable {
    pull?: () => Promise<any>;
}

export interface Watchable<MutationType extends Mutations.BaseMutation> {
    persistKey: Stores.Key<any>;
    peerId: string;
    watch(peerId: string): {
        mutations(observer: rxjs.Observer<MutationType>): Subscriptions;
    }
    fetch?: (ref: Elevated.Reference<MutationType>) => Promise<MutationType>;
    stateful(): undefined|Stateful<MutationType>;
    persistent(): undefined|Persistent<MutationType>;
}

export interface ScalarStream<ValueType, DomainType extends Elevated.Domain<ValueType> = Elevated.Domain<ValueType>> extends Pullable, Watchable<Mutations.OfScalar.Mutation<ValueType>>  {
    domain: DomainType;
    stateful(): undefined|(Stateful<Mutations.OfScalar.Mutation<ValueType>> & Clients.ScalarClient<ValueType>);
}

export interface SetStream<ValueType, DomainType extends Elevated.Domain<ValueType> = Elevated.Domain<ValueType>> extends Pullable, Watchable<Mutations.OfSet.Mutation<ValueType>> {
    domain: DomainType;
    stateful(): undefined|(Stateful<Mutations.OfSet.Mutation<ValueType>> & Clients.SetClient<ValueType>);
}

export interface SequenceStream<ValueType, DomainType extends Elevated.Domain<ValueType> = Elevated.Domain<ValueType>> extends Pullable, Watchable<Mutations.OfSequence.Mutation<ValueType>> {
    domain: DomainType;
    stateful(): undefined|(Stateful<Mutations.OfSequence.Mutation<ValueType>> & Clients.SequenceClient<ValueType>);
}

export type Collection<ValueType, DomainType extends Elevated.Domain<ValueType> = Elevated.Domain<ValueType>> = SetStream<ValueType, DomainType> | SequenceStream<ValueType, DomainType>;

export interface MapStream<KeyType, ValueType, DomainType extends Elevated.Domain<ValueType> = Elevated.Domain<ValueType>> extends Pullable, Watchable<Mutations.OfMap.Mutation<KeyType, ValueType>> {
    domain: DomainType;
    makeKey(): KeyType,
    chooseKeyFrom?: SetStream<KeyType, Elevated.Domain<KeyType>>;
    stateful(): undefined|(Stateful<Mutations.OfMap.Mutation<KeyType, ValueType>> & Clients.MapClient<KeyType, ValueType>);
}

export interface ValueStream<ValueType, DomainType extends Elevated.Domain<ValueType> = Elevated.Domain<ValueType>> {
    scalar?: ScalarStream<ValueType, DomainType>;
    set?: SetStream<ValueType, DomainType>;
    sequence?: SequenceStream<ValueType, DomainType>;
    map?: MapStream<any, ValueType, DomainType>;
}

export interface PropertyStream<PropertyTypeParams extends Properties.TypeParams> extends ValueStream<PropertyTypeParams["Value"], PropertyTypeParams["Domain"]> {
    descriptor: Properties.Descriptor<PropertyTypeParams>,
    instance: Properties.Instance<PropertyTypeParams>
}

export interface RelationStream<BindingType extends Objects.BindingType> extends Pullable, Watchable<Mutations.OfRelation.Mutation<BindingType>> {
    anchor: Relations.Anchor<BindingType>;
    stateful(): undefined|(Stateful<Mutations.OfRelation.Mutation<BindingType>> & Clients.RelationClient<BindingType>);
}

export type TypeParams<ValueType = any, KeyType = any, BindingType extends Objects.BindingType = any> = {
    Binding: BindingType,
    Value: ValueType,
    Key: KeyType
};

export type AnyStream<StreamTypeParams extends TypeParams> = {
    scalar?: ScalarStream<StreamTypeParams['Value']>;
    set?: SetStream<StreamTypeParams['Value']>;
    sequence?: SequenceStream<StreamTypeParams['Value']>;
    map?: MapStream<StreamTypeParams['Key'], StreamTypeParams['Value']>;
    relation?: RelationStream<StreamTypeParams['Binding']>
};

export abstract class Factory {
    abstract property<PropertyTypeParams extends Properties.TypeParams>(
        instance: Properties.Instance<PropertyTypeParams>
    ): PropertyStream<PropertyTypeParams>
    abstract relation<BindingType extends Objects.BindingType>(
        anchor: Relations.Anchor<BindingType>
    ): RelationStream<BindingType>;
}

export interface ViewStream<BindingType> {
    binding: BindingType;
    properties: Properties.Descriptor<Properties.TypeParams>[];
    property<PropertyTypeParams extends Properties.TypeParams>(descriptor: Properties.Descriptor<PropertyTypeParams>): PropertyStream<PropertyTypeParams>|undefined;
    relations: Relations.Descriptor<any>[];
    relation<BindingType extends Objects.BindingType>(descriptor: Relations.Descriptor<BindingType>): RelationStream<BindingType>|undefined;
}
